import { ReactElement, forwardRef, useEffect, useRef, useState } from 'react';
import { Swiper as ReactSwiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import useSwiperRef from './useSwiperRef';
import classnames from 'classnames';

interface SwiperProps extends SwiperOptions {
  slides: any[];
  renderSlide?: (slide: any, index?: number) => ReactElement;
  className?: string; // Add className property
  slideClassName?: (slide: any) => string; // Add slideClassName property
  navigation?: boolean; // Make navigation optional
  pagination?: boolean; // Make pagination optional
  arrowBorder?: boolean;
  blackBackground?: boolean;
  onSlideChange?: (swiper: SwiperType) => void;
}

const Swiper = forwardRef<SwiperRef, SwiperProps>(
  (
    {
      slides,
      renderSlide,
      className,
      slideClassName,
      navigation, // Destructure navigation
      pagination, // Destructure pagination
      arrowBorder,
      blackBackground,
      ...swiperProps
    }: SwiperProps,
    ref,
  ): ReactElement => {
    const [nextEl, nextElRef] = useSwiperRef();
    const [prevEl, prevElRef] = useSwiperRef();
    const paginationRef = useRef<HTMLDivElement | null>(null);

    const [swiperInstance, setSwiperInstance] = useState<any>(null);

    useEffect(() => {
      if (swiperInstance && paginationRef.current && pagination) {
        swiperInstance.params.pagination.el = paginationRef.current;

        if (swiperInstance.pagination) {
          swiperInstance.pagination.destroy();
          swiperInstance.pagination.init();
          swiperInstance.pagination.update();
        }
      }
    }, [swiperInstance, pagination]);
    return (
      <>
        <ReactSwiper
          ref={ref}
          modules={[Navigation, Pagination, A11y]}
          {...swiperProps}
          className={className} // Pass className to ReactSwiper
          touchEventsTarget="container"
          onSwiper={setSwiperInstance}
          navigation={
            navigation
              ? {
                  prevEl,
                  nextEl,
                }
              : undefined
          } // Conditionally include navigation
          pagination={
            pagination
              ? {
                  el: paginationRef.current,
                  type: 'fraction',
                  renderFraction: (currentClass, totalClass) =>
                    `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`,
                }
              : undefined
          } // Conditionally include pagination
        >
          {slides.map(slide => (
            <SwiperSlide key={slide.id} className={slideClassName ? slideClassName(slide) : ''}>
              {renderSlide(slide)}
            </SwiperSlide>
          ))}
        </ReactSwiper>
        {navigation && (
          <div className="swiper-actions swiper-buttons">
            <button
              type="button"
              className={classnames('swiper-button swiper-button-prev border', {
                border: arrowBorder,
                black: blackBackground,
              })}
              ref={prevElRef}
              aria-label="Previous button"
            >
              <i className="icon-arrow" />
            </button>
            {pagination && <div className="carousel-pages" ref={paginationRef} />}
            <button
              type="button"
              className={classnames('swiper-button swiper-button-next border', {
                border: arrowBorder,
                black: blackBackground,
              })}
              ref={nextElRef}
              aria-label="Next button"
            >
              <i className="icon-arrow" />
            </button>
          </div>
        )}
      </>
    );
  },
);

Swiper.displayName = 'Swiper';

export default Swiper;
